/*
 * Copyright (c) CIRROM 2026.
 */

import React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { LedgerField, normalizeValue } from "./helpers/normalize";
import { rampBandColor } from "./helpers/rampColor";
import classes from "./css/recordReview.module.css";

/*
 * What this record is, and what approving would make it.
 *
 * The ledger measures Cirrom's CONFIDENCE in each change; nothing measured
 * its IMPACT. Those are different axes, and on a sparse record the surface
 * was showing the wrong one: a contact holding nothing but an email address
 * gets first and last name derived - the change that makes it searchable,
 * mailable and displayable - and it rendered identically to a casing tweak,
 * under a "Moderate 55%" that undersold it.
 *
 * So this is arithmetic, not interpretation: how many fields the record
 * holds now, and how many it would hold if the pending proposals were
 * approved. Nothing is claimed about what those fields unlock; the numbers
 * move and the steward draws the conclusion.
 *
 * The delta wears the same ramp green as the approve control, so "the green
 * part is what saying yes adds" holds across the surface.
 */
export interface Completeness {
  /** Fields a source could vouch for - unknowns are excluded, not guessed. */
  total: number;
  populated: number;
  /** Fields a pending proposal would fill that are empty today. */
  wouldAdd: number;
  /** Fields no source could vouch for; excluded from the counts and named. */
  unknown: number;
}

/**
 * A proposal only counts toward wouldAdd when it carries an actual value:
 * some changes exist to report that Cirrom could NOT identify one, and those
 * fill nothing.
 */
export const measureCompleteness = (
  ledgerFields: LedgerField[],
  fullRecordFields: LedgerField[]
): Completeness | null => {
  const all = [...ledgerFields, ...fullRecordFields];
  if (all.length === 0) {
    return null;
  }
  /*
   * A field with no source to vouch for it is unknown rather than empty, and
   * a count that treats "we didn't load it" as "the CRM lacks it" would be a
   * confident lie. So unknowns are excluded from the arithmetic and reported
   * as their own number - the meter states what it measured and names what
   * it could not. With nothing known at all there is no number to show.
   */
  const known = all.filter((field) => !field.valueUnknown);
  if (known.length === 0) {
    return null;
  }
  return {
    total: known.length,
    populated: known.filter((field) => normalizeValue(field.crmValue)).length,
    wouldAdd: ledgerFields.filter(
      (field) =>
        !field.valueUnknown &&
        !normalizeValue(field.crmValue) &&
        field.candidates.some((candidate) => normalizeValue(candidate.value))
    ).length,
    unknown: all.length - known.length,
  };
};

export const CompletenessMeter = (props: {
  completeness: Completeness;
  /** Vendor name for the CRM behind this record; neutral wording without it. */
  crmLabel?: string;
}) => {
  const { total, populated, wouldAdd, unknown } = props.completeness;
  const projected = populated + wouldAdd;
  const crmLabel = props.crmLabel ?? "your CRM";

  return (
    <div className={classes.dossierSection}>
      <Typography
        variant="body2"
        component="h3"
        sx={{ color: "text.secondary", fontWeight: 600 }}
      >
        Record completeness
      </Typography>

      <Tooltip
        title={`${populated} of ${total} fields hold a value in ${crmLabel}${
          wouldAdd > 0
            ? `; approving the pending changes would fill ${wouldAdd} more`
            : ""
        }${
          unknown > 0
            ? `; ${unknown} field${
                unknown === 1 ? "" : "s"
              } could not be read from ${crmLabel}`
            : ""
        }`}
      >
        {/*
         * The bar restates the numbers beneath it, so it is hidden from
         * assistive tech rather than read out twice.
         */}
        <div className={classes.completenessBar} aria-hidden="true">
          <Box
            className={classes.completenessFilled}
            sx={{ flexGrow: populated, backgroundColor: "text.secondary" }}
          />
          {wouldAdd > 0 && (
            <Box
              className={classes.completenessFilled}
              sx={{
                flexGrow: wouldAdd,
                backgroundColor: rampBandColor("Outstanding"),
              }}
            />
          )}
          <Box
            className={classes.completenessFilled}
            sx={{
              flexGrow: Math.max(total - projected, 0),
              backgroundColor: "divider",
            }}
          />
        </div>
      </Tooltip>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {populated} of {total} fields
        {wouldAdd > 0 && (
          <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
            {" "}
            → {projected} if you approve
          </Box>
        )}
        {unknown > 0 && (
          <Tooltip
            title={`Fields this view could not read from ${crmLabel}; they may hold values.`}
          >
            <Box component="span">{` · ${unknown} unknown`}</Box>
          </Tooltip>
        )}
      </Typography>
    </div>
  );
};
